import '../app/environment.ts'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { create } from 'jss'
import { StyleSheetManager } from 'styled-components'
import { StylesProvider, jssPreset } from '@material-ui/core/styles'
import AppToastr from '@signal/components/baseline/AppToastr'
import { AppModalPromptGenerator } from '@signal/components/ModalPrompt/Generator'
import applyMiddleware from '@signal/core/baseline/data/middleware/apply-middleware'
import InstrumentationRoot from '@signal/core/instrumentation/Root'
import SWRRoot from '@signal/core/swr/Root'
import isTest from '@signal/utils/env/isTest'
import { setOnSite } from '@signal/utils/env/onSite'
import GlobalStyle from '@signal/theme/baseline/globalOnSiteStyle'
import ThemeProvider from '@signal/theme/Provider'
import loggly from '@signal/utils/loggly'
import { OnSiteAppComponentPropsType, StartOnSiteAppPropsType } from './types'

const SHADOW_ROOT_HOST_CLASS = 'pc_shadowRootHost'
const SHADOW_ROOT_INSTANCE_CLASS_PREFIX = 'pc_instance_'
const SHADOW_ROOT_CHILD_ID = 'pc_shadowRootChild'

const middlewareFetcher = applyMiddleware()(fetch)

/**
 * Baseline-derived apps should call this to render their application
 * @param {Component} ErrorPage - an optional catch-all error page to use
 * @param {Array} appRoutes - the APP_ROUTES from your baseline app
 * @param {String} menuBasePath - the base path for this application when in the menu
 * @param {Boolean} noTransitions - if true, don't use the transitions from react-transition-group on route change
 * ... also need to provide a number of react-router related components and hooks
 */
export const startOnSiteApp = (
  props: StartOnSiteAppPropsType
): React.FC<OnSiteAppComponentPropsType> => {
  const { appName, skipMount, OnSiteComponent } = props
  const startTime = new Date().getTime()
  let injectSelector = props.injectSelector

  setOnSite()

  // TODO: make sure we are logging correctly to loggly
  loggly.init()

  // the regular app, used in all cases
  const ConnectedApp: React.FC<{
    injectRoot: Element
    onSiteShadowRootChild?: Element
    stylesInPage: Element
  }> = ({ injectRoot, onSiteShadowRootChild, stylesInPage, ...rest }) => (
    <SWRRoot useCredentials={false} fetcher={middlewareFetcher()}>
      <InstrumentationRoot
        appName={props.appName}
        instrumentationId={props.instrumentationId}
        waitForPageInstrumentationFromApp={
          props.waitForPageInstrumentationFromApp
        }
      >
        {/* @ts-ignore */}
        <ThemeProvider onSiteShadowRootChild={onSiteShadowRootChild}>
          <GlobalStyle />
          <AppToastr>
            <AppModalPromptGenerator>
              <OnSiteComponent
                injectRoot={injectRoot}
                stylesInPage={stylesInPage}
                {...rest}
              />
            </AppModalPromptGenerator>
          </AppToastr>
        </ThemeProvider>
      </InstrumentationRoot>
    </SWRRoot>
  )

  if (!skipMount) {
    const appInstanceClass = `${SHADOW_ROOT_INSTANCE_CLASS_PREFIX}${appName.replace(
      / /g,
      '_'
    )}`
    if (!document.querySelector(`.${appInstanceClass}`)) {
      let injectRoot = injectSelector && document.querySelector(injectSelector)

      if (!injectRoot) {
        if (injectSelector) {
          console.warn(
            'Unable to find CSS selector',
            injectSelector,
            'to mount',
            appName
          )
        }
        injectSelector = undefined
        injectRoot = document.createElement('div')
        document.body.appendChild(injectRoot)
      }

      injectRoot.className += ` ${SHADOW_ROOT_HOST_CLASS} ${appInstanceClass}`

      // font-face directives need to be placed outside the shadow dom, so we will set up a style tag in which they can
      // be placed and pass it through to the on-site component.
      const stylesInPage = document.createElement('style')
      injectRoot.appendChild(stylesInPage)

      if (!isTest()) {
        // create a shadow root under this root for CSS encapsulation
        const shadowRoot = injectRoot.attachShadow({ mode: 'closed' })

        // need two levels of child nesting or else MUI gets upset
        const shadowRootHost = document.createElement('div')
        shadowRoot.appendChild(shadowRootHost)
        const shadowRootChild = document.createElement('div')
        shadowRootChild.id = SHADOW_ROOT_CHILD_ID
        shadowRootHost.appendChild(shadowRootChild)

        // needed so MUI styles get injected inside the shadow root
        const jss = create({
          ...jssPreset(),
          insertionPoint: shadowRootChild
        })

        // only used when injecting into the shadow root
        const ShadowApp = () => (
          <StyleSheetManager target={shadowRoot}>
            <StylesProvider jss={jss}>
              <ConnectedApp
                injectRoot={injectRoot as Element}
                stylesInPage={stylesInPage}
                onSiteShadowRootChild={shadowRootChild}
              />
            </StylesProvider>
          </StyleSheetManager>
        )

        ReactDOM.createRoot(shadowRootChild).render(<ShadowApp />)
      } else {
        ReactDOM.createRoot(injectRoot).render(
          <ConnectedApp injectRoot={injectRoot} stylesInPage={stylesInPage} />
        )
      }
    } else {
      console.log('already found instance of', appName, 'not re-injecting')
    }
  }

  loggly.info({ loadTime: Date.now() - startTime })

  return ConnectedApp
}

export default startOnSiteApp
